import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const Contact = () => {
  const navigate = useNavigate();

  const handleNavigateToContact = () => {
    navigate('/nosotros');
    window.scrollTo(0, 0);
  };

  // Team data mirroring the names/roles in the image
  const teamMembers = [
    {
      id: 1,
      name: 'Patricio Negrón',
      role: 'Chief Operations & Strategy Officer',
      image: 'patricio.jpeg',
    },
    {
      id: 2,
      name: 'Mariajosé Varela',
      role: 'Chief Executive Officer',
      image: 'mariajose.jpeg',
    },
    {
      id: 3,
      name: 'Alberto Varela',
      role: 'Chief Academic Officer',
      image: 'alberto.jpeg',
    },
    {
      id: 4,
      name: 'Mar Gutierrez',
      role: 'Chief Communications & Student Engagement Director',
      image: 'mar.jpeg',
    }
  ];

  // We duplicate the array to create the seamless loop effect (DESKTOP ONLY)
  const loopMembers = [...teamMembers, ...teamMembers];

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="bg-white py-20 overflow-hidden font-sans relative">
      {/* Decorative side fades to blend the carousel edges */}
      <div className="absolute top-0 left-0 w-32 h-full bg-gradient-to-r from-gray-100 to-transparent z-10 pointer-events-none hidden md:block" />
      <div className="absolute top-0 right-0 w-32 h-full bg-gradient-to-l from-gray-100 to-transparent z-10 pointer-events-none hidden md:block" />

      <motion.div 
        className="max-w-7xl mx-auto px-6 mb-16 text-center"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        {/* Badge */}
        <motion.div 
          className="inline-block px-8 py-1.5 border border-gray-400 rounded-full mb-8 bg-white/50 backdrop-blur-sm"
          variants={itemVariants}
        >
          <span className="text-gray-600 text-sm font-medium tracking-wide">Nosotros</span>
        </motion.div>

        {/* Title */}
        <motion.h2 
          className="text-3xl md:text-6xl font-bold text-[#1a3b5c] mb-6"
          variants={itemVariants}
        >
          Conoce a Nuestro Equipo
        </motion.h2>
        <motion.p 
          className="text-lg md:text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed"
          variants={itemVariants}
        >
            Diseñamos programas que conectan el talento local con oportunidades globales.
        </motion.p>
      </motion.div>

      {/* Main Container */}
      <motion.div 
        className="relative w-full"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.5 }}
        viewport={{ once: true }}
      >
        {/* --- MOBILE VIEW: Vertical Stack (No Animation, No Duplicates) --- */}
        <div className="flex flex-col items-center gap-12 px-4 md:hidden">
          {teamMembers.map((member, index) => (
             <div 
             key={`${member.id}-${index}-mobile`} 
             className="w-full max-w-[320px] flex-shrink-0 group cursor-pointer"
             onClick={handleNavigateToContact}
           >
             {/* Image Card */}
             <div className="h-[380px] rounded-[2.5rem] overflow-hidden mb-6 relative">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-full h-full scale-110 object-cover transition-transform duration-700 group-hover:scale-100"
                />
             </div>

             {/* Text Content */}
             <div className="text-left px-2">
               <h3 className="text-[#1a3b5c] font-bold text-2xl mb-1">
                 {member.name}
               </h3>
               <p className="text-[#6B7C8C] text-base font-light">
                 {member.role}
               </p>
             </div>
           </div>
          ))}
        </div>

        {/* --- DESKTOP VIEW: Horizontal Infinite Scroll (Original Logic) --- */}
        <div className="hidden md:flex gap-8 animate-scroll whitespace-nowrap px-4 w-max hover:pause">
          {loopMembers.map((member, index) => (
            <div 
              key={`${member.id}-${index}-desktop`} 
              className="w-[280px] md:w-[400px] flex-shrink-0 group cursor-pointer"
              onClick={handleNavigateToContact}
            >
              {/* Image Card */}
              <div className="h-[320px] md:h-[500px] rounded-[2.5rem] overflow-hidden mb-6 relative">
                 {/* Image */}
                 <img 
                   src={member.image} 
                   alt={member.name}
                   className="w-full h-full scale-110 object-cover transition-transform duration-700 group-hover:scale-100"
                 />
                 {/* Optional Overlay on hover */}
                 <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
              </div>

              {/* Text Content */}
              <div className="text-left px-2">
                <h3 className="text-[#1a3b5c] font-bold text-xl md:text-2xl mb-1 group-hover:text-[#5B9BB0] transition-colors">
                  {member.name}
                </h3>
                <p className="text-[#6B7C8C] text-sm md:text-base font-light">
                  {member.role}
                </p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Custom Keyframe Animation */}
      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        /* Only apply animation on larger screens if needed, 
           but since the container is hidden on mobile, 
           this global class is safe */
        .animate-scroll {
          animation: scroll 30s linear infinite;
        }

        
      `}</style>
    </section>
  );
};

export default Contact;